/**
 * MobileCompletarCurriculo - Página para completar dados curriculares
 * Permite adicionar cidade, estado, situação de emprego e outros dados
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { candidatesAPI, cnpjAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  Loader2, 
  ArrowLeft,
  MapPin,
  Building,
  Search,
  CheckCircle,
  Save,
  FileText,
  Briefcase
} from 'lucide-react';

const ESTADOS_BR = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' }
];

// Máscara de CNPJ
const formatCNPJ = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

export default function MobileCompletarCurriculo() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchingCNPJ, setSearchingCNPJ] = useState(false);
  const [empresaAtual, setEmpresaAtual] = useState(null);
  const [candidate, setCandidate] = useState(null);
  
  const [form, setForm] = useState({
    cidade: '',
    estado: '',
    estaTrabalhando: null,
    cnpjEmpresa: ''
  });

  useEffect(() => {
    loadCandidate();
  }, []);

  const loadCandidate = async () => {
    try {
      const response = await candidatesAPI.getMe();
      const data = response.data;
      setCandidate(data);
      
      // Preencher form com dados existentes
      setForm({
        cidade: data.cidade || '',
        estado: data.estado || '',
        estaTrabalhando: data.esta_trabalhando,
        cnpjEmpresa: data.cnpj_empresa_atual ? formatCNPJ(data.cnpj_empresa_atual) : ''
      });
      
      if (data.empresa_atual) {
        setEmpresaAtual(data.empresa_atual);
      }
    } catch (error) {
      console.error('Error loading candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'cnpjEmpresa') {
      value = formatCNPJ(value);
      if (empresaAtual) setEmpresaAtual(null);
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleBuscarCNPJ = async () => {
    const cnpj = form.cnpjEmpresa.replace(/\D/g, '');
    if (cnpj.length !== 14) {
      toast.error('CNPJ deve ter 14 dígitos');
      return;
    }
    
    setSearchingCNPJ(true);
    try {
      const response = await cnpjAPI.lookup(cnpj);
      setEmpresaAtual(response.data);
      toast.success(`Empresa encontrada: ${response.data.nome_fantasia || response.data.razao_social}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CNPJ não encontrado');
      setEmpresaAtual(null);
    } finally {
      setSearchingCNPJ(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        cidade: form.cidade.trim(),
        estado: form.estado,
        localizacao: form.cidade && form.estado ? `${form.cidade.trim()} - ${form.estado}` : '',
        esta_trabalhando: form.estaTrabalhando,
        cnpj_empresa_atual: form.estaTrabalhando && form.cnpjEmpresa 
          ? form.cnpjEmpresa.replace(/\D/g, '') 
          : null,
        empresa_atual: form.estaTrabalhando ? empresaAtual : null
      };

      await candidatesAPI.update(updateData);
      toast.success('Currículo atualizado com sucesso!');
      navigate('/m');
    } catch (error) {
      toast.error('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // Verificar se currículo está incompleto
  const isIncomplete = !form.cidade || !form.estado || form.estaTrabalhando === null;
  const progress = [form.cidade, form.estado, form.estaTrabalhando !== null].filter(Boolean).length;
  const progressPercent = Math.round((progress / 3) * 100);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800" data-testid="mobile-completar-curriculo">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-sm p-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/m')} className="text-slate-400">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-white">Completar Currículo</h1>
        </div>
      </div>

      <div className="px-4 py-6">
        <div className="max-w-md mx-auto space-y-6">

          {/* Progress Card */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-cyan-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">Progresso do Currículo</h3>
                  <p className="text-slate-400 text-sm">{progressPercent}% completo</p>
                </div>
              </div>
              <div className="mt-3 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-violet-500 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Form */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6 space-y-6">
              
              {/* Localização */}
              <div>
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-cyan-500" />
                  Localização
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-slate-300 text-sm">Cidade</Label>
                    <Input
                      type="text"
                      placeholder="Sua cidade"
                      value={form.cidade}
                      onChange={(e) => handleChange('cidade', e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-slate-300 text-sm">Estado</Label>
                    <select
                      value={form.estado}
                      onChange={(e) => handleChange('estado', e.target.value)}
                      className="w-full h-10 rounded-md bg-slate-700 border border-slate-600 px-3 text-sm text-white"
                    >
                      <option value="">UF</option>
                      {ESTADOS_BR.map(({ sigla }) => (
                        <option key={sigla} value={sigla}>{sigla}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Situação de Emprego */}
              <div>
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-cyan-500" />
                  Situação Atual
                </h3>
                
                <Label className="text-slate-300 text-sm mb-3 block">Você está trabalhando atualmente?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleChange('estaTrabalhando', true)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      form.estaTrabalhando === true
                        ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400'
                        : 'border-slate-600 bg-slate-700 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    <Building className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">Sim, estou</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleChange('estaTrabalhando', false);
                      setForm(prev => ({ ...prev, cnpjEmpresa: '' }));
                      setEmpresaAtual(null);
                    }}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      form.estaTrabalhando === false
                        ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400'
                        : 'border-slate-600 bg-slate-700 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    <Search className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">Não, em busca</span>
                  </button>
                </div>
              </div>

              {/* CNPJ da Empresa (se trabalhando) */}
              {form.estaTrabalhando === true && (
                <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <div className="space-y-1">
                    <Label className="text-slate-300 text-sm">CNPJ da empresa onde trabalha</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
                        <Input
                          type="text"
                          placeholder="00.000.000/0000-00"
                          value={form.cnpjEmpresa}
                          onChange={(e) => handleChange('cnpjEmpresa', e.target.value)}
                          maxLength={18}
                          className="pl-10 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={handleBuscarCNPJ}
                        disabled={searchingCNPJ || form.cnpjEmpresa.replace(/\D/g, '').length !== 14}
                        className="bg-cyan-600 hover:bg-cyan-700"
                      >
                        {searchingCNPJ ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Empresa encontrada */}
                  {empresaAtual && (
                    <div className="mt-3 p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-emerald-400 font-medium text-sm">
                            {empresaAtual.nome_fantasia || empresaAtual.razao_social}
                          </p>
                          {empresaAtual.endereco && (
                            <p className="text-slate-400 text-xs mt-1">
                              {empresaAtual.endereco.municipio}/{empresaAtual.endereco.uf}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Save Button */}
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-cyan-600 hover:bg-cyan-700 h-12"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Save className="w-5 h-5 mr-2" />
                )}
                Salvar Alterações
              </Button>

            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}
